'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Channel, dataService } from '@/lib/dataService';

export type UserRole = 'listener' | 'creator' | 'super_admin';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  avatar_url: string;
  channel?: Channel;
}

interface AuthContextType {
  user: UserProfile | null;
  role: UserRole;
  setRole: (role: UserRole) => void;
  loginAs: (role: UserRole) => void;
  logout: () => Promise<void>;
  currentChannel: Channel | null;
  refreshChannel: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRoleState] = useState<UserRole>('listener');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [currentChannel, setCurrentChannel] = useState<Channel | null>(null);

  const fetchUserChannel = async (userId: string, userRole: UserRole) => {
    if (userRole === 'creator' || userRole === 'super_admin') {
      try {
        const { data: channelData } = await supabase
          .from('channels')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (channelData) {
          setCurrentChannel(channelData as Channel);
          return;
        }
      } catch (err) {
        console.warn('Channel fetch from Supabase fallback:', err);
      }
      const channels = await dataService.getChannels();
      setCurrentChannel(channels[0] || null);
    } else {
      setCurrentChannel(null);
    }
  };

  useEffect(() => {
    // Initial session load & auth listener
    const initAuth = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const sessionUser = sessionData?.session?.user;

        if (sessionUser) {
          // Fetch profile from public.profiles
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', sessionUser.id)
            .single();

          const userRole: UserRole = profile?.role || 'creator';
          const userObj: UserProfile = {
            id: sessionUser.id,
            email: sessionUser.email || '',
            full_name: profile?.full_name || sessionUser.user_metadata?.full_name || sessionUser.email?.split('@')[0] || 'User',
            role: userRole,
            avatar_url: profile?.avatar_url || sessionUser.user_metadata?.avatar_url || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb',
          };

          setUser(userObj);
          setRoleState(userRole);
          await fetchUserChannel(sessionUser.id, userRole);
        } else {
          setUser(null);
          setCurrentChannel(null);
        }
      } catch (err) {
        console.warn('Supabase Auth init error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        const userRole: UserRole = profile?.role || 'creator';
        const userObj: UserProfile = {
          id: session.user.id,
          email: session.user.email || '',
          full_name: profile?.full_name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
          role: userRole,
          avatar_url: profile?.avatar_url || session.user.user_metadata?.avatar_url || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb',
        };

        setUser(userObj);
        setRoleState(userRole);
        await fetchUserChannel(session.user.id, userRole);
      } else {
        setUser(null);
        setRoleState('listener');
        setCurrentChannel(null);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const setRole = (newRole: UserRole) => {
    setRoleState(newRole);
    if (user) {
      setUser({ ...user, role: newRole });
      fetchUserChannel(user.id, newRole);
    }
  };

  const loginAs = (newRole: UserRole) => {
    setRoleState(newRole);
    if (newRole === 'super_admin') {
      const adminUser: UserProfile = {
        id: 'admin-1',
        email: 'superadmin@kissafm.com',
        full_name: 'Super Admin',
        role: 'super_admin',
        avatar_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb',
      };
      setUser(adminUser);
      fetchUserChannel(adminUser.id, 'super_admin');
    } else if (newRole === 'creator') {
      const creatorUser: UserProfile = {
        id: 'usr-1',
        email: 'creator@kissafm.com',
        full_name: 'Kavita Menon',
        role: 'creator',
        avatar_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d71d6fc6-1763295655575.png',
      };
      setUser(creatorUser);
      fetchUserChannel(creatorUser.id, 'creator');
    } else {
      const listenerUser: UserProfile = {
        id: 'usr-99',
        email: 'listener@gmail.com',
        full_name: 'Rajesh Listener',
        role: 'listener',
        avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
      };
      setUser(listenerUser);
      setCurrentChannel(null);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('Supabase signout info:', err);
    }
    setUser(null);
    setRoleState('listener');
    setCurrentChannel(null);
  };

  const refreshChannel = async () => {
    if (user) {
      await fetchUserChannel(user.id, role);
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, setRole, loginAs, logout, currentChannel, refreshChannel, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
